import React, { useState, useRef, useEffect, useCallback } from 'react';
import EmojiPicker from 'emoji-picker-react';
import { Smile } from 'lucide-react';

/**
 * Reusable Emoji Picker Button for the email template editor.
 *
 * Usage:
 *   <EmojiPickerButton onSelect={(emoji) => insertEmojiAt(emoji)} />
 *
 * Props:
 *   onSelect  — called with the emoji character string when the user picks one
 *   size      — icon size (default 16)
 *   className — extra class on the wrapper (optional)
 *   style     — extra inline style on the wrapper (optional)
 */
const EmojiPickerButton = ({ onSelect, size = 16, className = '', style }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  // Close on click-outside or Escape
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleEmojiClick = (emojiData) => {
    if (onSelect) onSelect(emojiData.emoji);
    setOpen(false);
  };

  return (
    <span
      ref={containerRef}
      className={`emtpl-emoji-wrapper ${className}`}
      style={{ position: 'relative', display: 'inline-flex', ...style }}
    >
      <button
        type="button"
        className="emtpl-emoji-btn"
        onClick={toggle}
        title="Insert emoji"
        aria-label="Insert emoji"
      >
        <Smile size={size} />
      </button>

      {open && (
        <div className="emtpl-emoji-picker-container">
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme="light"
            width={340}
            height={380}
            searchPlaceholder="Search emoji..."
            previewConfig={{ showPreview: false }}
            skinTonesDisabled
            lazyLoadEmojis
          />
        </div>
      )}
    </span>
  );
};

export default EmojiPickerButton;
